/**
 * IndexedDB Storage for Offline Support
 * Stores JSAs, Incidents, Training, and Sync Queue locally
 */

const DB_NAME = 'SafetyMindAI_Offline';
const DB_VERSION = 1;

let db = null;

// Initialize IndexedDB
export const initDB = () => {
  return new Promise((resolve, reject) => {
    if (db) {
      resolve(db);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('❌ Failed to open IndexedDB');
      reject(request.error);
    };

    request.onsuccess = () => {
      db = request.result;
      console.log('✅ IndexedDB initialized');
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const database = event.target.result;

      // JSAs store
      if (!database.objectStoreNames.contains('jsas')) {
        const jsaStore = database.createObjectStore('jsas', { keyPath: 'id' });
        jsaStore.createIndex('status', 'status', { unique: false });
        jsaStore.createIndex('companyId', 'companyId', { unique: false });
      }

      // Incidents store
      if (!database.objectStoreNames.contains('incidents')) {
        const incidentStore = database.createObjectStore('incidents', { keyPath: 'id' });
        incidentStore.createIndex('status', 'status', { unique: false });
        incidentStore.createIndex('companyId', 'companyId', { unique: false });
      }

      // Training/Courses store
      if (!database.objectStoreNames.contains('courses')) {
        const courseStore = database.createObjectStore('courses', { keyPath: 'id' });
        courseStore.createIndex('companyId', 'companyId', { unique: false });
      }

      // Training Progress store
      if (!database.objectStoreNames.contains('progress')) {
        const progressStore = database.createObjectStore('progress', { keyPath: 'id' });
        progressStore.createIndex('courseId', 'courseId', { unique: false });
        progressStore.createIndex('userId', 'userId', { unique: false });
      }

      // SDS Documents store
      if (!database.objectStoreNames.contains('sds')) {
        const sdsStore = database.createObjectStore('sds', { keyPath: 'id' });
        sdsStore.createIndex('companyId', 'companyId', { unique: false });
      }

      // Safety Audits store
      if (!database.objectStoreNames.contains('audits')) {
        const auditsStore = database.createObjectStore('audits', { keyPath: 'id' });
        auditsStore.createIndex('companyId', 'companyId', { unique: false });
        auditsStore.createIndex('status', 'status', { unique: false });
      }

      // Sync Queue - stores pending actions
      if (!database.objectStoreNames.contains('syncQueue')) {
        const syncStore = database.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('type', 'type', { unique: false });
        syncStore.createIndex('status', 'status', { unique: false });
        syncStore.createIndex('createdAt', 'createdAt', { unique: false });
      }

      // User data store
      if (!database.objectStoreNames.contains('userData')) {
        database.createObjectStore('userData', { keyPath: 'key' });
      }

      console.log('📦 IndexedDB schema created');
    };
  });
};

// Generic CRUD operations
export const saveToStore = async (storeName, data) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const saveBulkToStore = async (storeName, items) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    
    items.forEach(item => store.put(item));
    
    transaction.oncomplete = () => resolve(true);
    transaction.onerror = () => reject(transaction.error);
  });
};

export const getFromStore = async (storeName, id) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

export const getAllFromStore = async (storeName) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

export const deleteFromStore = async (storeName, id) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

export const clearStore = async (storeName) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
};

// Query by index
export const getByIndex = async (storeName, indexName, value) => {
  await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const index = store.index(indexName);
    const request = index.getAll(value);
    
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
};

// Sync Queue specific operations
export const addToSyncQueue = async (action) => {
  const queueItem = {
    ...action,
    status: 'pending',
    createdAt: new Date().toISOString(),
    retryCount: 0
  };
  return saveToStore('syncQueue', queueItem);
};

export const getPendingSyncItems = async () => {
  return getByIndex('syncQueue', 'status', 'pending');
};

export const markSyncItemComplete = async (id) => {
  const item = await getFromStore('syncQueue', id);
  if (item) {
    item.status = 'completed';
    item.syncedAt = new Date().toISOString();
    await saveToStore('syncQueue', item);
  }
};

export const markSyncItemFailed = async (id, error) => {
  const item = await getFromStore('syncQueue', id);
  if (item) {
    item.status = 'failed';
    item.error = error;
    item.retryCount = (item.retryCount || 0) + 1;
    await saveToStore('syncQueue', item);
  }
};

// User data helpers
export const saveUserData = async (key, value) => {
  return saveToStore('userData', { key, value, updatedAt: new Date().toISOString() });
};

export const getUserData = async (key) => {
  const result = await getFromStore('userData', key);
  return result?.value;
};

// Clear all offline data (for logout)
export const clearAllOfflineData = async () => {
  const stores = ['jsas', 'incidents', 'courses', 'progress', 'sds', 'audits', 'syncQueue', 'userData'];
  for (const store of stores) {
    await clearStore(store);
  }
  console.log('🗑️ All offline data cleared');
};

export default {
  initDB,
  saveToStore,
  saveBulkToStore,
  getFromStore,
  getAllFromStore,
  deleteFromStore,
  clearStore,
  getByIndex,
  addToSyncQueue,
  getPendingSyncItems,
  markSyncItemComplete,
  markSyncItemFailed,
  saveUserData,
  getUserData,
  clearAllOfflineData
};
